/**
 * Demos Chakra UI Theme
 *
 * Extends the default Chakra theme with custom colors and component styles
 */

import { extendTheme } from '@chakra-ui/react'
import colors from './colors'

const theme = extendTheme({
  colors: {
    // Add custom color palette
    brand: {
      50: colors.primary.pale,
      100: colors.primary.lighter,
      200: colors.primary.light,
      300: colors.primary.light,
      400: colors.primary.main,
      500: colors.primary.main,
      600: colors.primary.dark,
      700: colors.primary.dark,
      800: '#2e1345',
      900: '#1f0d2e',
    },
    // Override gray.50 to use our warm white background for page bg
    gray: {
      50: colors.background.main,  // #faf8fc - soft purple-tinted white for page backgrounds
      100: '#f7fafc',
      200: '#edf2f7',
      300: '#e2e8f0',
      400: '#cbd5e0',
      500: '#a0aec0',
      600: '#718096',
      700: '#4a5568',
      800: '#2d3748',
      900: '#1a202c',
    },
    // Lila / light-purple highlight palette (inherits from colors.lila)
    lila: {
      50: '#f3edfa',
      100: colors.lila.bg,   // #e2d5ef - main lila highlight
      200: '#d4c1e8',
      300: '#c3a9dd',
      400: '#a584c4',
      500: '#7a4fa3',
      600: colors.lila.text, // #502378 - purple text
      700: '#3d1a5c',
      800: '#2e1345',
      900: '#1f0d2e',
    },
    // Add a neutral gray for input/card backgrounds
    neutral: {
      50: '#f7f7f7',   // Light neutral gray (not pinkish)
      100: '#eeeeee',
      200: '#e0e0e0',
      300: '#bdbdbd',
      400: '#9e9e9e',
      500: '#757575',
      600: '#616161',
      700: '#424242',
      800: '#212121',
      900: '#121212',
    },
    // Background colors
    bg: {
      main: colors.background.main,
      white: colors.background.white,
      light: colors.background.light,
      gray: colors.background.gray,
    },
  },

  // Component style overrides
  components: {
    Button: {
      variants: {
        // Primary button style (main actions)
        primary: {
          bg: colors.button.primary,
          color: 'white',
          _hover: {
            bg: colors.button.primaryHover,
            _disabled: {
              bg: colors.button.disabled,
            },
          },
          _active: {
            bg: colors.button.primaryActive,
          },
          _disabled: {
            bg: colors.button.disabled,
            color: colors.button.disabledText,
            opacity: 0.6,
            cursor: 'not-allowed',
          },
        },
        // Secondary button style (less important actions)
        secondary: {
          bg: colors.button.secondary,
          color: 'white',
          _hover: {
            bg: colors.button.secondaryHover,
            _disabled: {
              bg: colors.button.disabled,
            },
          },
          _active: {
            bg: colors.button.secondaryActive,
          },
          _disabled: {
            bg: colors.button.disabled,
            color: colors.button.disabledText,
            opacity: 0.6,
            cursor: 'not-allowed',
          },
        },
        // Outline button style
        outline: {
          border: '1px solid',
          borderColor: colors.primary.main,
          color: colors.primary.main,
          _hover: {
            bg: colors.background.light,
          },
        },
        // Ghost button style
        ghost: {
          color: colors.text.primary,
          _hover: {
            bg: colors.button.ghostHover,
          },
        },
      },
      // Default button properties
      defaultProps: {
        colorScheme: 'brand',
      },
    },

    Input: {
      variants: {
        outline: {
          field: {
            bg: colors.form.inputBg,
            borderColor: colors.form.inputBorder,
            _hover: {
              borderColor: colors.ui.borderHover,
            },
            _focus: {
              borderColor: colors.form.inputBorderFocus,
              boxShadow: `0 0 0 1px ${colors.form.inputBorderFocus}`,
            },
            _placeholder: {
              color: colors.form.inputPlaceholder,
            },
          },
        },
      },
    },

    Textarea: {
      variants: {
        outline: {
          bg: colors.form.inputBg,
          borderColor: colors.form.inputBorder,
          _hover: {
            borderColor: colors.ui.borderHover,
          },
          _focus: {
            borderColor: colors.form.inputBorderFocus,
            boxShadow: `0 0 0 1px ${colors.form.inputBorderFocus}`,
          },
          _placeholder: {
            color: colors.form.inputPlaceholder,
          },
        },
      },
    },

    Link: {
      baseStyle: {
        color: colors.text.link,
        _hover: {
          color: colors.text.linkHover,
          textDecoration: 'underline',
        },
      },
    },

    Heading: {
      baseStyle: {
        color: colors.text.primary,
      },
    },

    Text: {
      baseStyle: {
        color: colors.text.primary,
      },
    },

    Card: {
      baseStyle: {
        container: {
          bg: colors.card.background,
          borderColor: colors.card.border,
          boxShadow: colors.card.shadow,
        },
      },
    },

    Table: {
      variants: {
        simple: {
          th: {
            bg: colors.table.headerBg,
            borderColor: colors.table.border,
            color: colors.text.primary,
          },
          td: {
            borderColor: colors.table.border,
          },
          tr: {
            _hover: {
              bg: colors.table.rowHover,
            },
          },
        },
      },
    },

    Checkbox: {
      baseStyle: {
        control: {
          borderColor: colors.form.inputBorder,
          _checked: {
            bg: colors.primary.main,
            borderColor: colors.primary.main,
            _hover: {
              bg: colors.primary.dark,
              borderColor: colors.primary.dark,
            },
          },
        },
      },
    },

    Radio: {
      baseStyle: {
        control: {
          borderColor: colors.form.inputBorder,
          _checked: {
            bg: colors.primary.main,
            borderColor: colors.primary.main,
            _hover: {
              bg: colors.primary.dark,
              borderColor: colors.primary.dark,
            },
          },
        },
      },
    },
  },

  // Global styles
  styles: {
    global: {
      body: {
        bg: colors.background.main,  // #faf8fc - soft purple-tinted white
        color: colors.text.primary,
      },
      // Set default Grid background to warm white
      '.chakra-grid': {
        bg: colors.background.main,
      },
    },
  },
})

export default theme
